import { useEffect, useRef, useCallback, useState } from "react";
import { io, Socket } from "socket.io-client";
import { IMessage } from "../types/message";

interface UseSocketProps {
  token: string;
  onNewMessage?: (_message: IMessage) => void;
  sendMessageSuccess?: (_message: IMessage) => void;
}

interface SendMessageResponse {
  success: boolean;
  message?: IMessage;
  error?: string;
}

export const useSocket = ({
  token,
  onNewMessage,
  sendMessageSuccess,
}: UseSocketProps) => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Correct mounted lifecycle
  const isMountedRef = useRef(false);

  // Avoid recreating socket when callbacks change
  const onNewMessageRef = useRef<UseSocketProps["onNewMessage"]>(onNewMessage);
  const sendMessageSuccessRef =
    useRef<UseSocketProps["sendMessageSuccess"]>(sendMessageSuccess);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    sendMessageSuccessRef.current = sendMessageSuccess;
  }, [sendMessageSuccess]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Create socket once
  useEffect(() => {
    const url = process.env.REACT_APP_PUBLIC_API_BASE_WS;
    if (!url) {
      console.error("[Socket] Missing REACT_APP_PUBLIC_API_BASE_WS");
      return;
    }

    const socket = io(url, {
      autoConnect: false, // we decide when to connect (based on token)
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      transports: ["polling", "websocket"],

      /**
       * Header-based auth:
       * - In browsers: headers usually work for polling, not for websocket upgrade.
       * - In RN / Node / Electron: can work for websocket too.
       */
      transportOptions: {
        polling: {
          extraHeaders: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
        websocket: {
          extraHeaders: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      },
    });

    socketRef.current = socket;

    const handleConnect = () => {
      if (isMountedRef.current) setIsConnected(true);
      // Useful to confirm actual transport being used
      const transport = socket.io.engine?.transport?.name;
      console.debug("[Socket] connected, transport =", transport);
    };

    const handleDisconnect = (reason: any) => {
      if (isMountedRef.current) setIsConnected(false);
      console.debug("[Socket] disconnected:", reason);
    };

    const handleNewMessage = (message: IMessage) => {
      if (!isMountedRef.current) return;
      onNewMessageRef.current?.(message);
    };

    const handleConnectError = (error: any) => {
      console.error("[Socket] connect_error:", error);
      if (isMountedRef.current) setIsConnected(false);
    };

    const handleError = (error: any) => {
      console.error("[Socket] error:", error);
      if (isMountedRef.current) setIsConnected(false);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("newMessage", handleNewMessage);
    socket.on("connect_error", handleConnectError);
    socket.on("error", handleError);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("newMessage", handleNewMessage);
      socket.off("connect_error", handleConnectError);
      socket.off("error", handleError);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]); // create once

  // Connect / reconnect when token changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    if (!token) {
      if (socket.connected) socket.disconnect();
      if (isMountedRef.current) setIsConnected(false);
      return;
    }

    // Update headers for next connection attempt
    // (Socket.IO reads these per-transport from options)
    socket.io.opts.transportOptions = {
      polling: { extraHeaders: { Authorization: `Bearer ${token}` } },
      websocket: { extraHeaders: { Authorization: `Bearer ${token}` } },
    };

    try {
      if (socket.connected) socket.disconnect();
      socket.connect();
    } catch (e) {
      console.warn("[Socket] reconnect failed", e);
    }
  }, [token]);

  const joinRoom = useCallback((groupId: string) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("joinRoom", groupId);
  }, []);

  const leaveRoom = useCallback((groupId: string) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit("leaveRoom", groupId);
  }, []);

  const sendMessage = useCallback(
    (data: { groupId: string; content: string; type?: string }) => {
      const socket = socketRef.current;
      if (!socket?.connected) {
        return Promise.reject(new Error("Socket is not connected"));
      }

      return new Promise<IMessage>((resolve, reject) => {
        let done = false;

        const t = window.setTimeout(() => {
          if (done) return;
          done = true;
          reject(new Error("sendMessage timeout waiting for server ack"));
        }, 10000);

        socket.emit("sendMessage", data, (response: SendMessageResponse) => {
          if (done) return;
          done = true;
          window.clearTimeout(t);

          if (!isMountedRef.current) return;

          if (response?.success && response?.message) {
            sendMessageSuccessRef.current?.(response.message);
            resolve(response.message);
          } else {
            reject(new Error(response?.error || "Failed to send message"));
          }
        });
      });
    },
    []
  );

  return { isConnected, joinRoom, leaveRoom, sendMessage };
};
